import type {
  CreateGramSocialShareInput,
  CreateGramWithStorageInput,
  GramCreationAcknowledgmentOutput,
  GramSocialShareOutput,
  GramSocialShareWithStorageOutput,
  GramStorageOutput,
  GramStorageUrlOutput,
} from '@sunrise-ui/api/events/api';
import { useSnackbar } from '@sunrise-ui/primitives';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { QueryKeys } from '../constants/QueryKeys';
import { gramsApi, useAuthMutation } from '../utils/api';

/**
 * Hook for creating a gram and storing it to AWS S3
 * Used to generate speaker grams when sessions are promoted to voting status
 */
export const useCreateGramWithStorage = () => {
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    mutationFn: async (
      input: CreateGramWithStorageInput,
    ): Promise<GramCreationAcknowledgmentOutput> => {
      const response = await gramsApi.createGramWithStorageGramsGeneratePost(
        {
          createGramWithStorageInput: input,
        },
        {
          // Extend timeout to 5 minutes for gram generation
          timeout: 300000, // 5 minutes in milliseconds
        },
      );
      return response.data;
    },
    onSuccess: () => {
      showSnackbar({
        text: 'Speaker gram generated successfully!',
        type: 'success',
      });
    },
    onError: (error) => {
      console.error('Error generating gram:', error);
      showSnackbar({
        text: 'Failed to generate speaker gram',
        type: 'error',
      });
    },
  });
};

const {
  gramsKey,
  byUserIdKey,
  fileUrlKey,
  socialSharesKey,
  bySlugKey,
  byUserEventKey,
} = QueryKeys.grams;

/**
 * Hook for fetching all grams for a specific user
 * Uses the GET /grams/users/{user_id}/ endpoint
 */
export const useGetUserGrams = (userId?: string) => {
  return useQuery({
    queryKey: [gramsKey, byUserIdKey, userId],
    queryFn: async (): Promise<GramStorageOutput[]> => {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const response = await gramsApi.getUserGramsGramsUsersUserIdGet({
        userId,
      });
      return response.data;
    },
    enabled: Boolean(userId),
    staleTime: 300000, // 5 minutes
  });
};

/**
 * Hook for getting gram storage information by user ID and event ID
 * Uses the GET /grams/users/{user_id}/events/{event_id}/ endpoint
 * Returns a single gram since there's only one gram per user per event
 * Caches 404 responses as null to prevent redundant API calls
 */
export const useGetGramByUserAndEvent = (
  userId?: string,
  eventId?: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: [gramsKey, byUserEventKey, userId, eventId],
    queryFn: async (): Promise<GramStorageOutput | null> => {
      try {
        const response =
          await gramsApi.getGramStorageByUserAndEventGramsUsersUserIdEventsEventIdGet(
            {
              userId: userId!,
              eventId: eventId!,
            },
          );
        return response.data;
      } catch (error: unknown) {
        // Cache 404 responses as null (no gram exists)
        if (isAxiosError(error) && error.response?.status === 404) {
          return null;
        }
        // Re-throw other errors so they're handled as failures
        throw error;
      }
    },
    enabled: !!userId && !!eventId && (options?.enabled ?? true),
    staleTime: 300000, // 5 minutes - cache both success and 404 responses
  });
};

/**
 * Hook for getting a temporary presigned URL for downloading a gram file
 * Uses the GET /grams/{gram_id}/url/ endpoint
 */
export const useGetGramFileUrl = (
  gramId?: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: [gramsKey, fileUrlKey, gramId],
    queryFn: async (): Promise<GramStorageUrlOutput> => {
      const response = await gramsApi.getGramFileUrlGramsGramIdUrlGet({
        gramId: gramId!,
      });
      return response.data;
    },
    enabled: !!gramId && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * Hook for creating a gram social share
 * Uses the POST /grams/social-shares/ endpoint
 */
export const useCreateGramSocialShare = () => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useAuthMutation({
    mutationFn: async (input: {
      createGramSocialShareInput: CreateGramSocialShareInput;
      userId: string;
    }): Promise<GramSocialShareOutput> => {
      const response =
        await gramsApi.createGramSocialShareGramsSocialSharesPost({
          createGramSocialShareInput: input.createGramSocialShareInput,
        });
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate specific social share query to refresh cache
      queryClient.invalidateQueries({
        queryKey: [
          socialSharesKey,
          variables.createGramSocialShareInput.session_id,
          variables.userId,
        ],
      });
    },
    onError: (error) => {
      console.error('Error creating gram social share:', error);
      showSnackbar({
        text: 'Failed to create social share link',
        type: 'error',
      });
    },
  });
};

/**
 * Hook for getting gram social share by session and user
 * Uses the GET /grams/social-shares/session/{session_id}/users/{user_id}/ endpoint
 * Caches 404 responses as null to prevent redundant API calls
 */
export const useGetGramSocialShareBySessionUser = (
  sessionId?: string,
  userId?: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: [socialSharesKey, sessionId, userId],
    queryFn: async (): Promise<GramSocialShareOutput | null> => {
      try {
        const response =
          await gramsApi.getGramSocialShareByUserAndSessionGramsSocialSharesSessionSessionIdUsersUserIdGet(
            {
              sessionId: sessionId!,
              userId: userId!,
            },
          );
        return response.data;
      } catch (error: unknown) {
        // Cache 404 responses as null (no social share exists)
        if (isAxiosError(error) && error.response?.status === 404) {
          return null;
        }
        // Re-throw other errors so they're handled as failures
        throw error;
      }
    },
    enabled: !!sessionId && !!userId && (options?.enabled ?? true),
    staleTime: 300000, // 5 minutes - cache both success and 404 responses
  });
};

/**
 * Hook for getting a gram social share by slug
 * Uses the GET /grams/social-shares/slugs/{slug}/ endpoint
 */
export const useGetGramSocialShareBySlug = (slug?: string) => {
  return useQuery({
    queryKey: [gramsKey, socialSharesKey, bySlugKey, slug],
    queryFn: async (): Promise<GramSocialShareWithStorageOutput> => {
      const response =
        await gramsApi.getGramSocialShareBySlugGramsSocialSharesSlugsSlugGet({
          slug: slug!,
        });
      return response.data;
    },
    enabled: !!slug,
  });
};
